import { forwardRef, type ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Icon } from '@/components/atoms/Icon';
import type { IconSize } from '@/components/atoms/Icon/Icon.types';
import { cn } from '@/utils/cn';
import styles from './IconButton.module.scss';

export interface IconButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  icon: LucideIcon;
  /** Tamaño del icono (interno). */
  size?: IconSize;
  /** Tamaño de la caja del botón. `md` = 40px (def.), `sm` ≈ 32px. */
  buttonSize?: 'sm' | 'md';
  variant?: 'default' | 'soft' | 'bare' | 'primary' | 'destructive';
  /** Estado activo (toggle), p. ej. bookmark guardado: icono relleno + color. */
  active?: boolean;
  /** Contenido superpuesto en la esquina (p. ej. punto/contador de avisos). */
  badge?: ReactNode;
  'aria-label': string;
  className?: string;
}

/**
 * Botón de solo-icono del design system (40×40 por defecto, transparente con
 * hover overlay). `forwardRef` para poder usarse con `<DialogClose asChild>` /
 * `<DrawerClose asChild>` de Radix.
 */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  {
    icon: LucideIcon,
    size = 'md',
    buttonSize = 'md',
    variant = 'default',
    active = false,
    badge,
    className,
    type = 'button',
    ...props
  },
  ref
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(
        styles.iconButton,
        variant !== 'default' && styles[`iconButton--${variant}`],
        buttonSize === 'sm' && styles['iconButton--sm'],
        active && styles['iconButton--active'],
        className
      )}
      {...props}
    >
      <Icon icon={LucideIcon} size={size} {...(active ? { fill: 'currentColor', strokeWidth: 0 } : {})} />
      {badge != null && <span className={styles.iconButton__badge}>{badge}</span>}
    </button>
  );
});
